// Copyright 2025-2026 - Hewlett Packard Enterprise Company
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { postQuery, getChatAsync } from '../actions/Api';
import { AuthContext } from '../contexts/AuthContext';
import useFlags from '../hooks/useFlags';

// Create the ChatContext
export const ChatContext = createContext();

// ChatProvider component
export function ChatProvider({ children }) {
  const { tenantAcid } = useContext(AuthContext);
  const [conversations, setConversations] = useState([]);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const activeChatIdRef = useRef(null);
  const queryInFlightRef = useRef(false);
  const cancelledRef = useRef(false);
  const prevTenantAcidRef = useRef(tenantAcid);
  const flags = useFlags();

  const createLoadingConversation = useCallback(
    query => ({
      query,
      answer: 'Loading',
      sessionId: null,
      showFollowUpQuestions: true,
      showFeedback: true,
    }),
    [],
  );

  const addConversation = useCallback(
    query => {
      cancelledRef.current = false;
      setConversations(prevConversations => [
        ...prevConversations,
        createLoadingConversation(query),
      ]);
    },
    [createLoadingConversation],
  );

  const updateConversation = useCallback(
    (index, answer, sessionId = null) => {
      setConversations(prevConversations => {
        const updatedConversations = [...prevConversations];
        if (index >= 0 && index < updatedConversations.length) {
          updatedConversations[index] = {
            ...updatedConversations[index],
            answer,
            sessionId: answer?.sessionId || sessionId || null,
            showFollowUpQuestions:
              !(
                flags.T_20260709_136074_HIDE_FOLLOW_UPS_IN_UI()?.[
                  answer?.dataSource?.toLowerCase?.()
                ] ?? false
              ) && (answer?.followupQuestions?.length ?? 0) > 0,
            showFeedback: true,
          };
          return updatedConversations;
        }
        return prevConversations;
      });
    },
    [flags],
  );

  const clearConversations = useCallback(() => {
    activeChatIdRef.current = null;
    queryInFlightRef.current = false;
    cancelledRef.current = true;
    setLoading(false);
    setConversations([]);
  }, []);

  // Cancel in-flight polling and clear conversations when tenant context
  // changes (e.g. user navigates back to MSP tenant selector, or switches
  // to a different tenant). For non-MSP users tenantAcid is always null
  // so this effect never fires.
  useEffect(() => {
    if (prevTenantAcidRef.current !== tenantAcid) {
      clearConversations();
      setIsFeedbackOpen(false);
    }
    prevTenantAcidRef.current = tenantAcid;
  }, [tenantAcid, clearConversations]);

  const hideAllFollowUpQuestions = useCallback(() => {
    setConversations(prevConversations =>
      prevConversations.map(conv => ({
        ...conv,
        showFollowUpQuestions: false,
      })),
    );
  }, []);

  const hideAllFeedback = useCallback(() => {
    setConversations(prevConversations =>
      prevConversations.map(conv => ({
        ...conv,
        showFeedback: false,
      })),
    );
  }, []);

  const getAsyncResponse = useCallback(async id => {
    // If conversations were cleared while postQuery was in-flight (e.g. tenant
    // changed), bail out to avoid overwriting the cancellation token and
    // resuming stale polling.
    if (cancelledRef.current) {
      return null;
    }
    activeChatIdRef.current = id;
    const maxRetries = 9;
    const pollingInterval = 5000; // 5 seconds

    let answer = null;

    for (let i = 0; i < maxRetries; i++) {
      // eslint-disable-next-line no-await-in-loop, no-promise-executor-return
      await new Promise(r => setTimeout(r, pollingInterval));

      // activeChatIdRef acts as a cancellation token — it's set to the
      // current request's id at the start of polling and cleared/overwritten
      // when conversations are reset. A mismatch means this request is stale.
      if (activeChatIdRef.current !== id) {
        return null;
      }

      try {
        // eslint-disable-next-line no-await-in-loop
        const chatResponse = await getChatAsync(id);

        if (
          chatResponse?.data &&
          chatResponse.data.summary !==
            'Chat response is being processed asynchronously.'
        ) {
          answer = chatResponse?.data;
          break;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error during polling:', error);
        break; // Exit the loop on error
      }
    }

    if (!answer) {
      throw new Error('Polling timed out');
    }

    return answer;
  }, []);

  const handleQueryClick = useCallback(
    async query => {
      if (queryInFlightRef.current) return;
      queryInFlightRef.current = true;
      setLoading(true);
      const index = conversations.length;
      const sessionId = conversations[conversations.length - 1]?.sessionId;
      let id = null;
      addConversation(query);
      setIsFeedbackOpen(false);
      try {
        const response = await postQuery(query, sessionId);
        id = response?.data?.id;
        const answer = await getAsyncResponse(id);

        if (answer) {
          updateConversation(index, answer);
          setLoading(false);
          queryInFlightRef.current = false;
        }
      } catch (error) {
        updateConversation(
          index,
          { summary: 'Error retrieving answer', id },
          sessionId,
        );
        setLoading(false);
        queryInFlightRef.current = false;
      }
    },
    [conversations, addConversation, updateConversation, getAsyncResponse],
  );

  const handleFreshQueryClick = useCallback(
    async query => {
      let id = null;
      queryInFlightRef.current = true;
      cancelledRef.current = false;
      setConversations([createLoadingConversation(query)]);
      setIsFeedbackOpen(false);
      try {
        const response = await postQuery(query, null);
        id = response?.data?.id;
        const answer = await getAsyncResponse(id);

        if (answer) {
          updateConversation(0, answer, null);
        }
      } catch (error) {
        updateConversation(0, { summary: 'Error retrieving answer', id }, null);
      } finally {
        queryInFlightRef.current = false;
      }
    },
    [createLoadingConversation, getAsyncResponse, updateConversation],
  );

  // Use useMemo to memoize the context value
  const contextValue = useMemo(
    () => ({
      conversations,
      addConversation,
      updateConversation,
      clearConversations,
      handleQueryClick,
      handleFreshQueryClick,
      hideAllFollowUpQuestions,
      getAsyncResponse,
      loading,
      setLoading,
      isFeedbackOpen,
      setIsFeedbackOpen,
      hideAllFeedback,
    }),
    [
      conversations,
      addConversation,
      updateConversation,
      clearConversations,
      handleQueryClick,
      handleFreshQueryClick,
      hideAllFollowUpQuestions,
      getAsyncResponse,
      loading,
      setLoading,
      isFeedbackOpen,
      setIsFeedbackOpen,
      hideAllFeedback,
    ],
  );

  return (
    <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>
  );
}

ChatProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
